import { chakra, Portal } from '@chakra-ui/react';
import { useEffect, useRef, type ReactNode } from 'react';

const Backdrop = chakra('div', {
  base: {
    position: 'fixed',
    inset: 0,
    bg: 'overlay.backdrop',
    zIndex: 'modal',
    animation: 'ehe-fade-in 120ms ease-out',
  },
});

const Panel = chakra('div', {
  base: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    maxWidth: '100vw',
    bg: 'surface.raised',
    color: 'text.primary',
    boxShadow: 'lg',
    zIndex: 'modal',
  },
});

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Edge the sheet slides in from (default right). */
  side?: 'right' | 'left';
  /** Panel width on >= sm screens; full-width below (default 26rem / 420px). */
  width?: string;
  /** Accessible name for the sheet surface (when there is no in-panel heading id). */
  label?: string;
  /** Id of an element inside that labels the sheet (takes precedence over `label`). */
  labelledBy?: string;
  /** Panel contents — the consumer builds its own header / body / footer. */
  children: ReactNode;
  /** Element focused on open; defaults to the sheet surface. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  'data-testid'?: string;
}

/**
 * A slide-in side panel (a.k.a. Drawer): portalled over a backdrop, anchored to the
 * left or right edge and full-height. Escape + backdrop-click dismiss, focus moves in
 * on open and is restored to the trigger on close, `role="dialog"` + `aria-modal`, and
 * body scroll is locked while open. Unopinionated inside — lay out header/body/footer
 * with the usual primitives.
 */
export function Sheet({
  open,
  onClose,
  side = 'right',
  width = '26rem',
  label,
  labelledBy,
  children,
  initialFocusRef,
  ...rest
}: SheetProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Keep the latest onClose in a ref so the open-effect can depend ONLY on `open`.
  // If `onClose` (typically an inline `() => setOpen(false)`) were an effect dep, the
  // effect would re-run on every parent render — and its focus-move would steal focus
  // from anything the user is typing into inside the sheet (e.g. the chat input).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    const id = requestAnimationFrame(() => {
      // Focus moves in on open — but NOT if the user already focused something inside
      // the sheet before this deferred frame ran (e.g. clicked the chat input the instant
      // it opened). Stealing it back to the panel is the "input loses focus on the first
      // keystroke" bug.
      if (surfaceRef.current?.contains(document.activeElement)) return;
      (initialFocusRef?.current ?? surfaceRef.current)?.focus();
    });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
    // Runs only on open/close transitions — NOT on every render. `initialFocusRef` is a
    // stable ref object; `onClose` is read via `onCloseRef` so it isn't a dependency.
  }, [open, initialFocusRef]);

  if (!open) return null;

  const edge = side === 'left' ? { left: 0 } : { right: 0 };
  const anim = side === 'left' ? 'ehe-sheet-in-left' : 'ehe-sheet-in-right';

  return (
    <Portal>
      <Backdrop
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <Panel
          ref={surfaceRef}
          role="dialog"
          aria-modal="true"
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy}
          tabIndex={-1}
          width={{ base: '100vw', sm: width }}
          animation={`${anim} 200ms cubic-bezier(0.2,0,0,1)`}
          borderLeftWidth={side === 'right' ? '1px' : undefined}
          borderRightWidth={side === 'left' ? '1px' : undefined}
          borderColor="border.default"
          {...edge}
          {...rest}
        >
          {children}
        </Panel>
      </Backdrop>
    </Portal>
  );
}
